import { noop } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { PAL_SDK_ENABLED } from '~/utils/env';
import { getPlayerManager } from './utils';

type LoadRequestData = framework.messages.LoadRequestData;

export interface PalConfig {
  /** Whether to allow storage (cookies, localStorage) for ads. Defaults to true. */
  allowStorage?: boolean;
  /** A URL describing the video stream content. */
  descriptionUrl?: string;
  /** The PPID (Publisher Provided Identifier) for the user. */
  ppid?: string;
  /** A unique session ID for the current playback session. */
  sessionId?: string;
  /** Custom logger function for debug messages. */
  logger?: (message: string) => void;
}

// Module-level state
let nonceLoader: goog.cast.pal.NonceLoader | undefined;
let nonceManager: goog.cast.pal.NonceManager | undefined;
let playbackDidStart = false;
let cleanupFn: (() => void) | undefined;
let logFn: (message: string) => void = noop;

function createPlayingHandler() {
  return () => {
    if (playbackDidStart) return;

    playbackDidStart = true;
    sendPlaybackStart();
  };
}

function createInteractionHandler() {
  return (interactionEvent: framework.events.Event) => {
    sendAdTouch(interactionEvent);
  };
}

function createMediaFinishedHandler() {
  return () => {
    sendPlaybackEnd();
  };
}

/**
 * Initializes the PAL SDK with consent settings.
 * Should be called once during receiver initialization.
 *
 * @param config - Configuration options for PAL
 */
export function initPalSdk(config: PalConfig = {}): void {
  if (!PAL_SDK_ENABLED) return;

  const { allowStorage = true, logger } = config;

  if (logger) {
    logFn = logger;
  }

  const consentSettings = new goog.cast.pal.ConsentSettings();
  consentSettings.allowStorage = allowStorage;

  nonceLoader = new goog.cast.pal.NonceLoader(consentSettings);
  logFn('[PAL] SDK initialized');

  // Set up video interaction tracking
  setupVideoInteractionTracking();
}

/**
 * Generates a nonce for the given load request.
 * Should be called in the LOAD message interceptor.
 *
 * @param loadRequestData - The load request data from the Cast receiver
 * @param config - Optional configuration overrides for this specific request
 * @returns The generated nonce string, or undefined if generation failed
 */
export async function generateNonce(
  loadRequestData: LoadRequestData,
  config: PalConfig = {},
): Promise<string | undefined> {
  if (!PAL_SDK_ENABLED) return undefined;

  if (!nonceLoader) {
    logFn('[PAL] Error: PAL SDK not initialized. Call initPalSdk() first.');
    return undefined;
  }

  nonceManager = undefined;
  playbackDidStart = false;

  const nonceRequest = new goog.cast.pal.NonceRequest();

  nonceRequest.adWillAutoPlay = true;
  nonceRequest.adWillPlayMuted = false;
  nonceRequest.descriptionUrl = loadRequestData.media?.contentUrl || '';
  nonceRequest.iconsSupported = true;
  nonceRequest.ppid = config.ppid || '';
  nonceRequest.sessionId = config.sessionId || uuid();
  nonceRequest.url = loadRequestData.media?.contentUrl || '';

  nonceRequest.videoHeight = window.devicePixelRatio * window.screen.height;
  nonceRequest.videoWidth = window.devicePixelRatio * window.screen.width;

  try {
    nonceManager = await nonceLoader.loadNonceManager(nonceRequest);
    const nonce = nonceManager.getNonce();
    logFn(`[PAL] Received nonce: ${nonce}`);
    return nonce;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    logFn(`[PAL] Error generating nonce: ${errorMessage}`);
    return undefined;
  }
}

/**
 * Returns the current nonce if available.
 * @returns The current nonce string, or undefined if not available
 */
export function getCurrentNonce(): string | undefined {
  return nonceManager?.getNonce();
}

/**
 * Returns the current NonceManager instance if available.
 * @returns The NonceManager instance, or undefined if not available
 */
export function getNonceManager(): goog.cast.pal.NonceManager | undefined {
  return nonceManager;
}

/**
 * Notifies PAL that playback has started.
 * This is automatically called when the PLAYING event fires.
 */
export function sendPlaybackStart(): void {
  if (nonceManager) {
    logFn('[PAL] Registered playback start');
    nonceManager.sendPlaybackStart();
  } else {
    logFn('[PAL] Error: No nonce manager available for playback start');
  }
}

/**
 * Notifies PAL that playback has ended.
 * This is automatically called when the MEDIA_FINISHED event fires.
 */
export function sendPlaybackEnd(): void {
  playbackDidStart = false;
  if (nonceManager) {
    logFn('[PAL] Registered playback end');
    nonceManager.sendPlaybackEnd();
  } else {
    logFn('[PAL] Error: No nonce manager available for playback end');
  }
}

/**
 * Notifies PAL of a user interaction with the player.
 * This is automatically called for various interaction events.
 *
 * @param event - The interaction event
 */
export function sendAdTouch(event: unknown): void {
  if (nonceManager) {
    logFn(`[PAL] Registered interaction: ${JSON.stringify(event)}`);
    nonceManager.sendAdTouch(event);
  } else {
    logFn('[PAL] Error: No nonce manager available for ad touch');
  }
}

/**
 * Notifies PAL that an ad was clicked.
 */
export function sendAdClick(): void {
  if (nonceManager) {
    logFn('[PAL] Registered ad click');
    nonceManager.sendAdClick();
  } else {
    logFn('[PAL] Error: No nonce manager available for ad click');
  }
}

/**
 * Sets up automatic video interaction tracking.
 * Registers event listeners for playback events and user interactions.
 */
function setupVideoInteractionTracking(): void {
  // Clean up any previous listeners
  if (cleanupFn) {
    cleanupFn();
  }

  const playerManager = getPlayerManager();

  const handlePlaying = createPlayingHandler();
  const handleInteraction = createInteractionHandler();
  const handleMediaFinished = createMediaFinishedHandler();

  const interactionEvents = [
    cast.framework.events.EventType.REQUEST_SEEK,
    cast.framework.events.EventType.REQUEST_STOP,
    cast.framework.events.EventType.REQUEST_PAUSE,
    cast.framework.events.EventType.REQUEST_PLAY,
    cast.framework.events.EventType.REQUEST_SKIP_AD,
    cast.framework.events.EventType.REQUEST_PLAY_AGAIN,
    cast.framework.events.EventType.REQUEST_PLAYBACK_RATE_CHANGE,
    cast.framework.events.EventType.REQUEST_VOLUME_CHANGE,
    cast.framework.events.EventType.REQUEST_USER_ACTION,
    cast.framework.events.EventType.REQUEST_FOCUS_STATE,
  ];

  // Add event listeners
  playerManager.addEventListener(
    cast.framework.events.EventType.PLAYING,
    handlePlaying,
  );

  playerManager.addEventListener(interactionEvents, handleInteraction);

  playerManager.addEventListener(
    cast.framework.events.EventType.MEDIA_FINISHED,
    handleMediaFinished,
  );

  // Store cleanup function
  cleanupFn = () => {
    playerManager.removeEventListener(
      cast.framework.events.EventType.PLAYING,
      handlePlaying,
    );

    // Remove interaction event listeners
    for (const eventType of interactionEvents) {
      playerManager.removeEventListener(eventType, handleInteraction);
    }

    playerManager.removeEventListener(
      cast.framework.events.EventType.MEDIA_FINISHED,
      handleMediaFinished,
    );
  };
}

/**
 * Cleans up PAL SDK resources.
 * Should be called when the receiver is shutting down.
 */
export function cleanupPalSdk(): void {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = undefined;
  }
  nonceManager = undefined;
  nonceLoader = undefined;
  playbackDidStart = false;
  logFn('[PAL] SDK cleaned up');
}
